import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Quote } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import ClientMarquee from "@/components/ClientMarquee";

const values = [
  {
    label: "01 · Systems > Guesswork",
    title: "Every step tracked, every metric owned.",
    desc: "We measure 5 things every week — clicks, bookings, show-ups, close rates, revenue. If a number moves the wrong way, we know which lever to pull.",
  },
  {
    label: "02 · Revenue > Vanity",
    title: "Views don't pay bills. Pipeline does.",
    desc: "We don't sell reach, followers or impressions as outcomes. We sell qualified calls, closed clients and top-line revenue you can bank.",
  },
  {
    label: "03 · Founders > Faceless Brands",
    title: "Personal brand is the moat.",
    desc: "In 2026, buyers trust founders, not logos. We help you become the name people search for — and the reason they choose you over cheaper competitors.",
  },
];

export default function AboutPage() {
  return (
    <div>
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-20 md:pt-20 md:pb-28">
          <Reveal>
            <div className="eyebrow mb-6">About BrandChilly</div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-5xl">
              Built on <em className="gold-shine font-medium not-italic">systems</em>.
              <br />
              <span className="text-zinc-300">Tested in the </span>
              <span className="text-red-500">real world.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-zinc-400 text-base md:text-lg leading-relaxed font-body">
              BrandChilly is a high-ticket client acquisition partner for founders, coaches and agencies.
              We combine personal branding with an AI-powered Digital Sales Engine — because in this decade,
              trust and automation compound faster than any ad budget.
            </p>
          </Reveal>
        </div>
      </section>

      {/* STORY */}
      <section className="relative py-20 border-t border-white/5 tl-alt">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="eyebrow mb-5">Our story</div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">
                Most businesses don't have an offer problem.
                <br /><em className="gold-shine font-medium not-italic">They have a system problem.</em>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-6 text-zinc-400 text-base md:text-lg leading-relaxed font-body">
            <Reveal delay={0.15}>
              <p>
                BrandChilly was founded by <span className="text-white">Saurabh Khaspuri</span> — a brand
                strategist with 15+ years in brand & direct-response growth, ex-CMO of a ₹100 Cr+ brand,
                and mentor to 3,000+ professionals.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <p>
                After watching hundreds of talented founders spend on ads that didn't convert, chase leads
                that didn't buy and post content that didn't move revenue, one pattern emerged: nobody had
                a <span className="text-white">system</span>. They had tactics, hopes and Instagram Reels —
                but no closed loop from stranger to paying client.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p>
                Marketing shouldn't feel like a monthly gamble. We don't sell vanity metrics, views or empty
                leads. We build <span className="text-white">predictable, measurable systems</span> where
                every step is tracked, optimised and connected directly to top-line revenue.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="relative py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-red-600/30 via-red-500/10 to-transparent blur-2xl pointer-events-none" />
              <div className="relative luxury-card overflow-hidden p-2">
                <img
                  src="/assets/founder.jpg"
                  alt="Saurabh Khaspuri — Founder, BrandChilly"
                  className="w-full h-auto rounded-2xl object-cover"
                  data-testid="founder-image"
                />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-white border border-[#E4E4E7] shadow-[0_4px_24px_rgba(0,0,0,0.06)] rounded-2xl px-5 py-3 hidden md:block">
                <div className="font-serif-display text-2xl">15+ years</div>
                <div className="text-xs text-zinc-500 tracking-wider uppercase">Brand & growth</div>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="eyebrow mb-5">Meet the founder</div>
            </Reveal>
            <Reveal delay={0.15}>
              <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05]">
                Saurabh <em className="gold-shine font-medium not-italic">Khaspuri</em>.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-zinc-400 text-base md:text-lg leading-relaxed font-body max-w-xl">
                15+ years building brands. Ex-CMO of a ₹100 Cr+ business. 3,000+ professionals trained.
                Saurabh leads every BrandChilly engagement personally — from Growth Audit to the day your
                acquisition engine starts humming.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
                <div className="luxury-card p-4">
                  <div className="font-serif-display text-3xl text-white">15+</div>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-zinc-500 mt-1">Years</div>
                </div>
                <div className="luxury-card p-4">
                  <div className="font-serif-display text-3xl text-white">3.54M</div>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-zinc-500 mt-1">YT Reach</div>
                </div>
                <div className="luxury-card p-4">
                  <div className="font-serif-display text-3xl text-white">3,000+</div>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-zinc-500 mt-1">Trained</div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.4}>
              <div className="mt-10 luxury-card p-7 relative">
                <Quote size={22} className="text-red-500 mb-4" />
                <p className="font-serif-display text-2xl md:text-3xl leading-snug">
                  "Every marketing problem is really a system problem in disguise. Fix the system, and revenue stops being a monthly guess."
                </p>
                <div className="mt-5 text-xs tracking-[0.2em] uppercase text-zinc-500">— Saurabh Khaspuri, Founder</div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="relative py-24 md:py-32 border-t border-white/5 tl-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="eyebrow mb-5">What we believe</div>
            <h2 className="font-serif-display text-4xl md:text-6xl max-w-3xl leading-[1.05]">
              Three convictions we <em className="gold-shine font-medium not-italic">run on</em>.
            </h2>
          </Reveal>

          <StaggerGroup className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {values.map((v, i) => (
              <StaggerItem key={i}>
                <div className="luxury-card p-8 h-full">
                  <div className="eyebrow mb-4">{v.label}</div>
                  <h3 className="font-serif-display text-2xl md:text-3xl mb-4 leading-tight">{v.title}</h3>
                  <p className="text-zinc-400 font-body leading-relaxed">{v.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* CLIENTS MARQUEE */}
      <ClientMarquee eyebrow="Proof across industries" title="Brands that trusted the system." testId="about-clients" className="tl-alt" />

      {/* CTA */}
      <section className="relative py-24 md:py-32 border-t border-white/5 tl-red">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05]">
              Ready to build a <em className="gold-shine font-medium not-italic">system</em> that pays you back?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-zinc-400 font-body max-w-xl mx-auto">
              30-minute Growth Audit. We map your current funnel, identify the leak, and show you what your
              Digital Sales Engine would look like once installed.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/book" className="btn-primary mt-10" data-testid="about-book-call-btn">
              Book your Growth Audit <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
